/**
 * Partner Portal Service
 * 
 * Service สำหรับเรียก Partner Portal Cloud Functions
 */

import { callCloudFunction, login as parseLogin, validateSession } from '@/services/adapters/parse/parse-client'
import type {
  PartnerUser,
  PartnerLoginResponse,
  GetPartnerDashboardResponse,
  GetPartnerReferralsResponse,
  GetPartnerReportResponse,
  GetPartnerCommissionSummaryResponse,
  GetPartnerReferralEarningsResponse
} from './partner-portal.types'

// Storage keys
const STORAGE_KEYS = {
  SESSION_TOKEN: 'partnerSessionToken',
  USER_DATA: 'partnerUser',
}

export class PartnerPortalService {
  // ============================================
  // Authentication
  // ============================================

  /**
   * Login สำหรับ Partner (admin_group = 3 หรือ 4)
   */
  static async login(username: string, password: string): Promise<PartnerLoginResponse> {
    try {
      console.log('🔐 [Partner] Attempting login:', username)
      
      // 1. Login ผ่าน Parse
      const result = await parseLogin(username, password)
      
      if (!result.success || !result.user) {
        return {
          success: false,
          error: result.error || 'ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง'
        }
      }
      
      // 2. ตรวจสอบว่าเป็น Partner หรือไม่ (admin_group = 3 หรือ 4)
      const adminGroup = result.user.admin_group
      if (adminGroup !== 3 && adminGroup !== 4) {
        console.warn('🚫 [Partner] User is not a partner:', adminGroup)
        return {
          success: false,
          error: 'บัญชีนี้ไม่ใช่บัญชีพันธมิตร'
        }
      }
      
      // 3. สร้าง Partner User object
      const partnerUser: PartnerUser = {
        objectId: result.user.objectId,
        username: result.user.username,
        email: result.user.email,
        firstname: result.user.firstname,
        lastname: result.user.lastname,
        phone: result.user.phone,
        admin_group: adminGroup,
        sessionToken: result.user.sessionToken,
      }
      
      // 4. เก็บ session
      this.saveSession(partnerUser)
      
      console.log('✅ [Partner] Login successful:', partnerUser.username)
      
      return {
        success: true,
        user: partnerUser
      }
    } catch (error: any) {
      console.error('❌ [Partner] Login error:', error)
      return {
        success: false,
        error: error.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่'
      }
    }
  }

  /**
   * ตรวจสอบ session และ restore user
   */
  static async validateAndRestoreSession(): Promise<PartnerUser | null> {
    try {
      const sessionToken = localStorage.getItem(STORAGE_KEYS.SESSION_TOKEN)
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA)
      
      if (!sessionToken || !userData) {
        return null
      }
      
      // Validate session with server
      const result = await validateSession(sessionToken)
      
      if (!result.success || !result.user) {
        this.clearSession()
        return null
      }
      
      // Check if still a partner
      const adminGroup = result.user.admin_group
      if (adminGroup !== 3 && adminGroup !== 4) {
        this.clearSession()
        return null
      }
      
      // Return saved user data with validated session
      return JSON.parse(userData)
    } catch (error) {
      console.error('❌ [Partner] Session validation error:', error)
      this.clearSession()
      return null
    }
  }

  /**
   * Logout
   */
  static logout(): void {
    this.clearSession()
  }

  /**
   * Get current partner user (from local storage)
   */
  static getCurrentUser(): PartnerUser | null {
    try {
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA)
      return userData ? JSON.parse(userData) : null
    } catch {
      return null
    }
  }

  /**
   * Get session token
   */
  static getSessionToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.SESSION_TOKEN)
  }

  // ============================================
  // Dashboard
  // ============================================

  /**
   * ดึงข้อมูล Dashboard
   */
  static async getDashboard(): Promise<GetPartnerDashboardResponse> {
    const sessionToken = this.getSessionToken()
    if (!sessionToken) {
      return { success: false, error: 'กรุณาเข้าสู่ระบบ' }
    }

    try {
      const result = await callCloudFunction('getPartnerDashboard', {}, sessionToken)
      return result
    } catch (error: any) {
      console.error('❌ [Partner] getDashboard error:', error)
      return { success: false, error: error.message }
    }
  }

  // ============================================
  // Referrals
  // ============================================

  /**
   * ดึงรายการสมาชิกที่แนะนำ
   */
  static async getReferrals(params: {
    level?: 'L1' | 'L2' | 'all'
    limit?: number
    skip?: number
    search?: string
  } = {}): Promise<GetPartnerReferralsResponse> {
    const sessionToken = this.getSessionToken()
    if (!sessionToken) {
      return { success: false, error: 'กรุณาเข้าสู่ระบบ' }
    }

    try {
      const result = await callCloudFunction('getPartnerReferrals', {
        level: params.level || 'all',
        limit: params.limit || 50,
        skip: params.skip || 0,
        search: params.search
      }, sessionToken)
      return result
    } catch (error: any) {
      console.error('❌ [Partner] getReferrals error:', error)
      return { success: false, error: error.message }
    }
  }

  // ============================================
  // Reports
  // ============================================

  /**
   * ดึงรายงานตามช่วงเวลา
   */
  static async getReport(params: {
    start_date: string
    end_date: string
    include_realtime?: boolean
  }): Promise<GetPartnerReportResponse> {
    const sessionToken = this.getSessionToken()
    if (!sessionToken) {
      return { success: false, error: 'กรุณาเข้าสู่ระบบ' }
    }

    try {
      const result = await callCloudFunction('getPartnerReport', {
        start_date: params.start_date,
        end_date: params.end_date,
        include_realtime: params.include_realtime ?? true
      }, sessionToken)
      return result
    } catch (error: any) {
      console.error('❌ [Partner] getReport error:', error)
      return { success: false, error: error.message }
    }
  }

  // ============================================
  // Commission Summary
  // ============================================

  /**
   * ดึงสรุป Commission
   */
  static async getCommissionSummary(): Promise<GetPartnerCommissionSummaryResponse> {
    const sessionToken = this.getSessionToken()
    if (!sessionToken) {
      return { success: false, error: 'กรุณาเข้าสู่ระบบ' }
    }

    try {
      const result = await callCloudFunction('getPartnerCommissionSummary', {}, sessionToken)
      return result
    } catch (error: any) {
      console.error('❌ [Partner] getCommissionSummary error:', error)
      return { success: false, error: error.message }
    }
  }

  // ============================================
  // Referral Earnings
  // ============================================

  /**
   * ดึงรายได้แยกตามคนที่แนะนำ
   */
  static async getReferralEarnings(params: {
    level?: 'L1' | 'L2' | 'all'
    limit?: number
    skip?: number
    order?: 'earnings' | 'recent'
  } = {}): Promise<GetPartnerReferralEarningsResponse> {
    const sessionToken = this.getSessionToken()
    if (!sessionToken) {
      return { success: false, error: 'กรุณาเข้าสู่ระบบ' }
    }

    try {
      const result = await callCloudFunction('getPartnerReferralEarnings', {
        level: params.level || 'all',
        limit: params.limit || 50,
        skip: params.skip || 0,
        order: params.order || 'earnings'
      }, sessionToken)
      return result
    } catch (error: any) {
      console.error('❌ [Partner] getReferralEarnings error:', error)
      return { success: false, error: error.message }
    }
  }

  // ============================================
  // Private Helpers
  // ============================================

  private static saveSession(user: PartnerUser): void {
    localStorage.setItem(STORAGE_KEYS.SESSION_TOKEN, user.sessionToken)
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user))
  }

  private static clearSession(): void {
    localStorage.removeItem(STORAGE_KEYS.SESSION_TOKEN)
    localStorage.removeItem(STORAGE_KEYS.USER_DATA)
  }
}
